import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.scss';

// Components
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import HeroSection from './components/HeroSection/HeroSection';
import MissionSection from './components/MissionSection/MissionSection';
import EnergyProjectsMap from './components/EnergyProjectsMap/EnergyProjectsMap';
import KeyStatistics from './components/KeyStatistics/KeyStatistics';
import EnergyFinancingTrends from './components/EnergyFinancingTrends/EnergyFinancingTrends';
import ProjectImpactOverview from './components/ProjectImpactOverview/ProjectImpactOverview';
import ProjectImpactOverviewBar from './components/ProjectImpactOverviewBar/ProjectImpactOverviewBar';
import StoriesFromField from './components/StoriesFromField/StoriesFromField';
import KeyStatisticsSimple from './components/KeyStatisticsSimple/KeyStatisticsSimple';
import BannerSlider from './components/BannerSlider/BannerSlider';
import DataGlance from './components/DataGlance/DataGlance';
import ElectricityAccessChart from './components/ElectricityAccessChart/ElectricityAccessChart';
import SustainableImpactSnapshots from './components/SustainableImpactSnapshots/SustainableImpactSnapshots';
import AboutEnergyTracker from './components/AboutEnergyTracker/AboutEnergyTracker';
import PartnersCollaborations from './components/PartnersCollaborations/PartnersCollaborations';
import AboutPage from './components/AboutPage/AboutPage';
import MethodologyPage from './pages/MethodologyPage/MethodologyPage';
import ImpactInsightsPage from './pages/ImpactInsightsPage/ImpactInsightsPage';
import BlogPostPage from './components/BlogPostPage/BlogPostPage';
import ProjectLocationsMap from './components/ProjectLocationsMap/ProjectLocationsMap';
import EnergyFinancingChart from './components/EnergyFinancingChart/EnergyFinancingChart';
import HouseholdElectrificationChart from './components/HouseholdElectrificationChart/HouseholdElectrificationChart';
import ProjectDatabaseTable from './components/ProjectDatabaseTable/ProjectDatabaseTable';
import HomePageV1 from './pages/HomePageV1/HomePageV1';
import HomePageV2 from './pages/HomePageV2/HomePageV2';
import Chatbot from './components/Chatbot/Chatbot';
import CountryEnergyProfile from './pages/CountryEnergyProfile/CountryEnergyProfile';
import CountryEnergyProfileV1 from './pages/CountryEnergyProfileV1/CountryEnergyProfileV1';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage/PrivacyPolicyPage';
import ContactPage from './pages/ContactPage/ContactPage';
import ChatbotPage from './pages/ChatbotPage/ChatbotPage';
import CleanCookingPage from './pages/CleanCookingPage/CleanCookingPage';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import AllProjectsPage from './pages/AllProjectsPage/AllProjectsPage';
import GoogleTranslate from './components/GoogleTranslate/GoogleTranslate';
import { Helmet } from 'react-helmet-async';
import { SITE_BASE_URL } from './config/environments/api';

// ScrollToTop component to handle scroll behavior on route changes
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// ScrollToTopButton component for manual scroll to top
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <button
      className={`scroll-to-top-btn ${isVisible ? 'visible' : ''}`}
      onClick={scrollToTop}
      aria-label="Scroll to top"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
};

const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>Africa Energy Trends | Energy Access, Clean Cooking, Investment & Development Insights</title>
        <meta property="og:title" content="Africa Energy Trends | Energy Access, Clean Cooking, Investment & Development Insights" />
        <meta property="og:description" content="Explore Africa's evolving energy landscape with data-driven insights on clean energy, electricity access, financing trends, and development impacts across all African regions. Track renewable growth, policy progress, and investment flows shaping the future of Africa's energy sector." />
        <meta property="og:image" content={`${SITE_BASE_URL}/og-images/homepage.png`} />
        <meta property="og:image:secure_url" content={`${SITE_BASE_URL}/og-images/homepage.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`${SITE_BASE_URL}`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Africa Energy Access Trends" />
        <meta name="description" content="Explore Africa's evolving energy landscape with data-driven insights on clean energy, electricity access, financing trends, and development impacts across all African regions. Track renewable growth, policy progress, and investment flows shaping the future of Africa's energy sector." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Africa Energy Trends | Energy Access, Clean Cooking, Investment & Development Insights" />
        <meta name="twitter:description" content="Explore Africa's evolving energy landscape with data-driven insights on clean energy, electricity access, financing trends, and development impacts across all African regions." />
        <meta name="twitter:image" content={`${SITE_BASE_URL}/og-images/homepage.png`} />
      </Helmet>
      <div>
        <BannerSlider />
        <DataGlance />
        <ElectricityAccessChart />
        <EnergyFinancingTrends />
        <EnergyProjectsMap />
        <SustainableImpactSnapshots />
      </div>
    </>
  );
};


const DataInsightsPage = () => {
  const [selectedCountry, setSelectedCountry] = useState('All Countries');
  const [selectedInstitution, setSelectedInstitution] = useState('All Institutions');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);

  const countries = ['All Countries', 'Kenya', 'Morocco', 'Nigeria', 'South Africa', 'Egypt'];
  const institutions = ['All Institutions', 'World Bank', 'AfDB', 'Other IFIs'];

  const handleExportData = () => {
    // Create a simple CSV export functionality
    const csvContent = "data:text/csv;charset=utf-8," + 
      "Project Name,Country,Sector,Investment,Impact,Status\n" +
      "BrightSun Initiative,Kenya,Solar Energy,$10M,100000 homes,Active\n" +
      "Rural Electrification Project,Uganda,Grid Extension,$5M,50000 homes,Completed\n" +
      "Clean Cookstove Initiative,Nigeria,Clean Cooking,$2M,200000 households,Active\n" +
      "Hydro Power Development,Rwanda,Renewable Energy,$20M,20000 homes,Active\n" +
      "Wind Energy Project,Ethiopia,Wind Energy,$15M,150000 homes,Completed";
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "africa_energy_projects_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="data-dashboard-page">
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">Data Dashboard</h1>
          <p className="dashboard-subtitle">Explore energy access and clean energy finance data across Africa</p>
        </div>
        <div className="global-filters">
          <div className="dropdown-container">
            <button 
              className="filter-btn dropdown-btn"
              onClick={() => {
                setShowCountryDropdown(!showCountryDropdown);
                setShowInstitutionDropdown(false);
              }}
            >
              {selectedCountry}
              <span className="dropdown-arrow">▼</span>
            </button>
            {showCountryDropdown && (
              <div className="dropdown-menu">
                {countries.map(country => (
                  <button
                    key={country}
                    className={`dropdown-item ${selectedCountry === country ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedCountry(country);
                      setShowCountryDropdown(false);
                    }}
                  >
                    {country}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="dropdown-container">
            <button 
              className="filter-btn dropdown-btn"
              onClick={() => {
                setShowInstitutionDropdown(!showInstitutionDropdown);
                setShowCountryDropdown(false);
              }}
            >
              {selectedInstitution}
              <span className="dropdown-arrow">▼</span>
            </button>
            {showInstitutionDropdown && (
              <div className="dropdown-menu">
                {institutions.map(institution => (
                  <button
                    key={institution}
                    className={`dropdown-item ${selectedInstitution === institution ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedInstitution(institution);
                      setShowInstitutionDropdown(false);
                    }}
                  >
                    {institution}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button className="filter-btn export-btn" onClick={handleExportData}>
            📥 Export Data
          </button>
        </div>
      </div>
      
      <div className="dashboard-grid">
        <div className="dashboard-row-1">
          <div className="map-section">
            <ProjectLocationsMap />
          </div>
        </div>
        
        <div className="dashboard-row-2">
          <div className="chart-section">
            <EnergyFinancingChart />
          </div>
          <div className="chart-section">
            <HouseholdElectrificationChart />
          </div>
  </div>
        
        <div className="dashboard-row-3">
          <ProjectDatabaseTable />
        </div>
      </div>
  </div>
);
};

const ResourcesPageV1 = () => (
  <div className="page-content">
      <div className="impact-insights-section">
        <div className="section-header">
          <h1 className="section-title">Impact Insights</h1>
          <p className="section-subtitle">Analyzing the real-world impact of energy access and clean energy projects</p>
        </div>
        <ProjectImpactOverviewBar />
      </div>
    
    <div className="qualitative-impact-section">
      <div className="section-header">
        <h1 className="section-title">Qualitative Impact Analysis</h1>
        <p className="section-subtitle">Select a country to view detailed qualitative impacts</p>
      </div>
      
      <div className="country-tabs">
        <button className="tab-btn active">Kenya</button>
        <button className="tab-btn">Tanzania</button>
        <button className="tab-btn">Ethiopia</button>
        <button className="tab-btn">Ghana</button>
        <button className="tab-btn">Zambia</button>
      </div>
      
      <div className="country-overview">
        <h2 className="overview-title">Kenya Impact Overview</h2>
        <p className="overview-description">
          Kenya has been a leader in renewable energy adoption in East Africa, with significant investments in geothermal, wind, and solar energy. Energy access projects have particularly targeted rural communities and productive use applications.
        </p>
      </div>
      
      <div className="impact-categories">
        <div className="impact-card">
          <h3 className="card-title">Income & Livelihoods Impact</h3>
          <ul className="impact-list">
            <li>20% average increase in household income after electrification</li>
            <li>Extended business hours for small shops and markets</li>
            <li>New agricultural processing capabilities</li>
            <li>Enhanced irrigation systems powered by renewable energy</li>
          </ul>
        </div>
        
        <div className="impact-card">
          <h3 className="card-title">Job Creation & Training</h3>
          <ul className="impact-list">
            <li>12,500+ direct jobs created in renewable energy sector</li>
            <li>48% of new renewable jobs held by youth under 35</li>
            <li>Training programs established in 18 counties</li>
            <li>Women comprise 35% of the renewable energy workforce</li>
          </ul>
        </div>
      </div>
      
      <div className="featured-projects">
        <h2 className="projects-title">Featured Projects</h2>
        <div className="projects-grid">
          <div className="project-card">
            <h3 className="project-name">Lake Turkana Wind Power</h3>
            <p className="project-description">Africa's largest wind farm, providing 310MW to Kenya's national grid.</p>
            <p className="project-beneficiaries"><strong>Beneficiaries:</strong> 1.1M households</p>
          </div>
          
          <div className="project-card">
            <h3 className="project-name">Garissa Solar Plant</h3>
            <p className="project-description">50MW solar power plant in northeastern Kenya, built with Chinese investment.</p>
            <p className="project-beneficiaries"><strong>Beneficiaries:</strong> 350,000 households</p>
          </div>
          
          <div className="project-card">
            <h3 className="project-name">M-KOPA Solar</h3>
            <p className="project-description">Pay-as-you-go solar home system provider with innovative financing.</p>
            <p className="project-beneficiaries"><strong>Beneficiaries:</strong> 750,000 households</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);


// Component to handle loader on route changes
const RouteChangeLoader = ({ setIsLoading }) => {
  const location = useLocation();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    // On initial load, use the existing logic
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }

    // On route change, show loader
    setIsLoading(true);

    let minDisplayTime = 800; // Minimum time to show loader (0.8 seconds for smooth UX)
    let startTime = Date.now();
    let isHiding = false;

    const hideLoader = () => {
      if (isHiding) return;
      isHiding = true;
      
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);
      
      setTimeout(() => {
        setIsLoading(false);
      }, remainingTime);
    };

    // Check if content is visible and ready
    const checkContentReady = () => {
      // Wait for React to render
      setTimeout(() => {
        const mainContent = document.querySelector('.main-content');
        const header = document.querySelector('header');
        
        // Check if main content exists and has some height (is rendered)
        if (mainContent && (mainContent.offsetHeight > 0 || header)) {
          // Additional check: wait for images to start loading
          setTimeout(() => {
            hideLoader();
          }, 200);
        } else {
          // If content not ready, check again
          setTimeout(checkContentReady, 100);
        }
      }, 200);
    };

    // Start checking content ready
    checkContentReady();

    // Fallback: hide loader after maximum wait time (3 seconds)
    const maxWaitTimer = setTimeout(() => {
      hideLoader();
    }, 3000);

    return () => {
      clearTimeout(maxWaitTimer);
    };
  }, [location.pathname, setIsLoading, isInitialLoad]);

  return null;
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let minDisplayTime = 1000; // Minimum time to show loader (1 second for smooth UX)
    let startTime = Date.now();
    let isHiding = false;

    const hideLoader = () => {
      if (isHiding) return;
      isHiding = true;
      
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);
      
      setTimeout(() => {
        setIsLoading(false);
      }, remainingTime);
    };

    // Check if content is visible and ready
    const checkContentReady = () => {
      // Wait for React to render
      setTimeout(() => {
        const mainContent = document.querySelector('.main-content');
        const header = document.querySelector('header');
        
        // Check if main content exists and has some height (is rendered)
        if (mainContent && (mainContent.offsetHeight > 0 || header)) {
          // Additional check: wait for images to start loading
          setTimeout(() => {
            hideLoader();
          }, 200);
        } else {
          // If content not ready, check again
          setTimeout(checkContentReady, 100);
        }
      }, 200);
    };

    // Wait for window to fully load
    const handleWindowLoad = () => {
      checkContentReady();
    };

    // Check if already loaded
    if (document.readyState === 'complete') {
      checkContentReady();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    // Also start checking after a short delay (in case load event is slow)
    const initialCheck = setTimeout(() => {
      if (document.readyState === 'complete') {
        checkContentReady();
      }
    }, 500);

    // Fallback: hide loader after maximum wait time (5 seconds)
    const maxWaitTimer = setTimeout(() => {
      hideLoader();
    }, 5000);

    return () => {
      window.removeEventListener('load', handleWindowLoad);
      clearTimeout(initialCheck);
      clearTimeout(maxWaitTimer);
    };
  }, []);

  return (
    <HelmetProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div className="App">
        <RouteChangeLoader setIsLoading={setIsLoading} />
        {isLoading && (
          <div className="app-loader">
            <div className="loader-container">
              <img 
                src="/spinner/circle-spinner.gif" 
                alt="Loading..." 
                className="loader-spinner"
              />
            </div>
          </div>
        )}
        <ScrollToTop />
        <Header />
        <GoogleTranslate />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/home-v1" element={<HomePageV1 />} />
            <Route path="/home-v2" element={<HomePageV2 />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/country-energy-profile/:country?" element={<CountryEnergyProfile />} />
            <Route path="/country-energy-profile-v1" element={<CountryEnergyProfileV1 />} />
            <Route path="/impact-insights" element={<ImpactInsightsPage />} />
            <Route path="/impact-insights-v1" element={<ResourcesPageV1 />} />
            <Route path="/contact-us" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/chat-bot" element={<ChatbotPage />} />
            <Route path="/clean-cooking" element={<CleanCookingPage />} />
            <Route path="/projects" element={<AllProjectsPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
        <Chatbot />
        <ScrollToTopButton />
      </div>
    </Router>
    </HelmetProvider>
  );
}

export default App;